import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import {
  faBan,
  faChartColumn,
  faClock,
  faCoins,
  faMoneyBillTrendUp,
  faReceipt,
  faRotateRight,
  faSackDollar,
  faUtensils,
} from "@fortawesome/free-solid-svg-icons"
import { useRodizio } from "../../hooks/Rodizio/useRodizio"
import { convertToLocalMoney, fromInputDate } from "../../Objects/Generics/Helps"
import { companyMemory } from "../../Objects/Memory/Memory"
import type { RodizioMetrics } from "../../Objects/Rodizio/RodizioMetrics"
import { getRestaurantNow } from "../../utils/timeZone"
import { CashFlowMetricsLoading } from "./CashFlowMetricsLoading"
import { CashFlowPeriodFilterPopup } from "./CashFlowPeriodFilterPopup"
import { buildCompactLabelValueListHtml, buildSummaryCardsHtml, openPrintReport } from "../../utils/printReport"
import { FinancialPageLayout } from "./components/FinancialPageLayout"
import { FinancialPageHeader } from "./components/FinancialPageHeader"
import { FinancialKpiCard as MetricCard } from "./components/FinancialKpiCard"
import { FinancialChartCard } from "./components/FinancialChartCard"
import { FinancialDonutChart } from "./components/charts/FinancialDonutChart"
import { FinancialHorizontalBarChart } from "./components/charts/FinancialHorizontalBarChart"
import type { FinancialTone } from "./types/financialUi"
import { CashFlowPrintFormatPopup, type CashFlowPrintFormat } from "./CashFlowPrintFormatPopup"

type RodizioMetricCard = {
  key: string
  value: string
  subtitle?: string
  icon: IconProp
  tone?: FinancialTone
}

export const RodizioMetricsPage: React.FC = () => {
  const { t } = useTranslation("rodizio")

  const api = useRodizio()

  const apiRef = useRef(api)

  const latestRequestRef = useRef(0)

  const [today] = useState(() => getRestaurantNow(companyMemory.timeZoneId))

  const [startDate, setStartDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1).toInputDate())

  const [endDate, setEndDate] = useState(today.toInputDate())

  const [metrics, setMetrics] = useState<RodizioMetrics | null>(null)

  const [isLoading, setIsLoading] = useState(false)

  const [showPeriodFilter, setShowPeriodFilter] = useState(false)

  const [showPrintFormatPopup, setShowPrintFormatPopup] = useState(false)

  const [refreshNonce, setRefreshNonce] = useState(0)

  const hasValidPeriod = Boolean(startDate && endDate && startDate <= endDate)

  useEffect(() => {
    apiRef.current = api
  }, [api])

  const loadMetrics = useCallback(async () => {
    const requestId = ++latestRequestRef.current

    if (!hasValidPeriod) {
      setMetrics(null)

      setIsLoading(false)

      return
    }

    setMetrics(null)

    setIsLoading(true)

    try {
      const periodMetrics = await apiRef.current.getMetrics(startDate, endDate)

      if (periodMetrics && latestRequestRef.current === requestId) setMetrics(periodMetrics)
    } finally {
      if (latestRequestRef.current === requestId) setIsLoading(false)
    }
  }, [endDate, hasValidPeriod, startDate])

  useEffect(() => {
    const timer = window.setTimeout(() => void loadMetrics(), 0)

    return () => {
      window.clearTimeout(timer)

      latestRequestRef.current += 1
    }
  }, [loadMetrics, refreshNonce])

  const handleApplyPeriodFilter = (filters: { dateStart: string; dateEnd: string }, forceRefresh?: boolean) => {
    if (forceRefresh && filters.dateStart === startDate && filters.dateEnd === endDate) {
      setRefreshNonce((currentValue) => currentValue + 1)
    }

    setStartDate(filters.dateStart)

    setEndDate(filters.dateEnd)

    setShowPeriodFilter(false)
  }

  const metricCards = useMemo<RodizioMetricCard[]>(
    () =>
      metrics
        ? [
            {
              key: "gross_revenue",
              value: convertToLocalMoney(metrics.grossRevenue),
              icon: faSackDollar,
              tone: "brand",
            },
            {
              key: "net_revenue",
              value: convertToLocalMoney(metrics.netRevenue),
              subtitle: t("metrics.open_revenue") + ": " + convertToLocalMoney(metrics.openRevenue),
              icon: faMoneyBillTrendUp,
              tone: "positive",
            },
            {
              key: "open_revenue",
              value: convertToLocalMoney(metrics.openRevenue),
              subtitle: t("metrics.canceled_revenue") + ": " + convertToLocalMoney(metrics.canceledRevenue),
              icon: faClock,
              tone: "warning",
            },
            {
              key: "canceled_revenue",
              value: convertToLocalMoney(metrics.canceledRevenue),
              icon: faBan,
              tone: "negative",
            },
            {
              key: "access_revenue",
              value: convertToLocalMoney(metrics.accessRevenue),
              icon: faReceipt,
              tone: "info",
            },
            {
              key: "paid_additionals_revenue",
              value: convertToLocalMoney(metrics.paidAdditionalsRevenue),
              icon: faCoins,
              tone: "violet",
            },
            {
              key: "sessions",
              value: String(metrics.sessions),
              subtitle: t("metrics.active_sessions") + ": " + metrics.activeSessions,
              icon: faRotateRight,
            },
            {
              key: "included_items",
              value: String(metrics.includedItems),
              subtitle: t("metrics.average_items") + ": " + metrics.averageItemsPerSession.toLocaleString("pt-BR"),
              icon: faUtensils,
              tone: "positive",
            },
            {
              key: "catalog_value",
              value: convertToLocalMoney(metrics.includedCatalogValue),
              icon: faChartColumn,
              tone: "info",
            },
          ]
        : [],
    [metrics, t],
  )

  const handlePrintReport = (format: CashFlowPrintFormat) => {
    if (!metrics) return

    const summaryCards = metricCards.map((card) => ({
      label: t(`metrics.${card.key}`),
      value: card.value,
      subtitle: card.subtitle,
    }))

    const bodyHtml =
      format === "thermal" ? buildCompactLabelValueListHtml(summaryCards) : buildSummaryCardsHtml(summaryCards)

    openPrintReport({
      title: t("metrics.title"),
      subtitle: [
        companyMemory.companyName || t("pages:cashflow.print_report.restaurant_fallback"),
        t("pages:cashflow.print_report.period_label", {
          startDate: fromInputDate(startDate).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(endDate).toLocaleDateString("pt-BR"),
        }),
        t("pages:cashflow.print_report.generated_at_label", {
          dateTime: getRestaurantNow(companyMemory.timeZoneId).toLocaleDateTimeOrDash(
            "pt-BR",
            companyMemory.timeZoneId,
          ),
        }),
      ].join(" • "),
      bodyHtml,
      format,
    })
  }

  const handleSelectPrintFormat = (format: CashFlowPrintFormat) => {
    setShowPrintFormatPopup(false)
    handlePrintReport(format)
  }

  const revenueComposition = metrics
    ? [
        { label: t("metrics.access_revenue"), value: metrics.accessRevenue, color: "#f97316" },
        { label: t("metrics.paid_additionals_revenue"), value: metrics.paidAdditionalsRevenue, color: "#8b5cf6" },
      ]
    : []

  const channelDistribution = metrics
    ? [
        { label: t("metrics.waiter_channel"), value: metrics.waiterItems, color: "#f97316" },
        { label: t("metrics.tablet_channel"), value: metrics.tableTabletItems, color: "#3b82f6" },
        { label: t("metrics.customer_channel"), value: metrics.customerMobileItems, color: "#10b981" },
      ]
    : []

  return (
    <FinancialPageLayout>
      {showPeriodFilter && (
        <CashFlowPeriodFilterPopup
          initialFilters={{ dateStart: startDate, dateEnd: endDate }}
          today={today}
          onApplyFilters={handleApplyPeriodFilter}
          onClose={() => setShowPeriodFilter(false)}
        />
      )}

      {showPrintFormatPopup && (
        <CashFlowPrintFormatPopup
          onSelectFormat={handleSelectPrintFormat}
          onClose={() => setShowPrintFormatPopup(false)}
        />
      )}

      <FinancialPageHeader
        title={t("metrics.title")}
        subtitle={t("metrics.subtitle")}
        period={t("pages:cashflow.print_report.period_label", {
          startDate: fromInputDate(startDate).toLocaleDateString("pt-BR"),
          endDate: fromInputDate(endDate).toLocaleDateString("pt-BR"),
        })}
        filterLabel={t("pages:cashflow.filter_action")}
        printLabel={t("pages:cashflow.print_action")}
        isLoading={isLoading}
        isPrintDisabled={!metrics}
        onFilter={() => setShowPeriodFilter(true)}
        onPrint={() => setShowPrintFormatPopup(true)}
        help={{
          helpKey: "rodizioMetrics",
          title: t("helpPopup:global.rodizioMetrics.title"),
          description: t("helpPopup:global.rodizioMetrics.description"),
          bullets: [
            ...(t("helpPopup:global.rodizioMetrics.steps", { returnObjects: true }) as string[]),
            ...(t("helpPopup:global.rodizioMetrics.bullets", { returnObjects: true }) as string[]),
          ],
        }}
      />

      <CashFlowMetricsLoading isVisible={isLoading} />

      {!isLoading && !hasValidPeriod && (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-700">
          {t("metrics.invalid_period")}
        </div>
      )}

      {!isLoading && hasValidPeriod && (
        <>
          <div>
            <h2 className="mb-3 text-sm font-black uppercase tracking-[0.12em] text-slate-500">
              {t("metrics.financial_section")}
            </h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
              {metricCards.slice(0, 6).map((card) => (
                <MetricCard
                  key={card.key}
                  title={t(`metrics.${card.key}`)}
                  value={card.value}
                  icon={card.icon}
                  tone={card.tone}
                  subtitle={card.subtitle}
                />
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-3 text-sm font-black uppercase tracking-[0.12em] text-slate-500">
              {t("metrics.operational_section")}
            </h2>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              {metricCards.slice(6).map((card) => (
                <MetricCard
                  key={card.key}
                  title={t(`metrics.${card.key}`)}
                  value={card.value}
                  icon={card.icon}
                  tone={card.tone}
                  subtitle={card.subtitle}
                />
              ))}
            </div>
          </div>

          {metrics && (
            <>
              <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <FinancialChartCard
                  title={t("metrics.revenue_composition_title")}
                  subtitle={t("metrics.revenue_composition_subtitle")}
                >
                  <FinancialDonutChart
                    data={revenueComposition}
                    ariaLabel={t("metrics.revenue_composition_aria")}
                    emptyTitle={t("metrics.chart_empty")}
                    formatValue={convertToLocalMoney}
                  />
                </FinancialChartCard>

                <FinancialChartCard title={t("metrics.channel_chart_title")} subtitle={t("metrics.channel_chart_subtitle")}>
                  <FinancialHorizontalBarChart
                    data={channelDistribution}
                    ariaLabel={t("metrics.channel_chart_aria")}
                    emptyTitle={t("metrics.chart_empty")}
                    formatValue={(value) => value.toLocaleString("pt-BR")}
                    valueLabel={t("metrics.included_items")}
                  />
                </FinancialChartCard>
              </div>

              <aside className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm font-semibold leading-6 text-blue-900">
                {t("metrics.financial_note")}
              </aside>
            </>
          )}
        </>
      )}
    </FinancialPageLayout>
  )
}
